import { isValid, differenceInMinutes } from 'date-fns';
import { toDisplayDate } from '../../../utils/timezoneUtils';
import { CalendarEvent as EngineEvent, TaskScope } from '@/types';
import { expandRecurringEvents } from './recurrence';

/**
 * 🧠 CALENDAR ENGINE v2.0 - HYBRID SCOPE EDITION
 * Orchestrates UTC moments into User "Wall Clock" time.
 * Handles siloing between /users/{uid} and /households/{hid}.
 */
export class CalendarEngine {
  private slotDurationMinutes: number; // Example property

  constructor(slotDurationMinutes: number) {
    this.slotDurationMinutes = slotDurationMinutes;
  }

  processInstances(
    events: EngineEvent[],
    userTimezone: string,
    currentUserId: string,
    householdAddress: string | null,
    isTravelEnabled: boolean,
    visibleRange?: { start: Date; end: Date },
    travelDurations?: Map<string, number>
  ): EngineEvent[] {
    let processedInstances: EngineEvent[] = [];
    let previousDisplayInstance: EngineEvent | null = null;

    // --- 0. RECURRENCE EXPANSION ---
    // Master events (recurrence.length > 0) never render directly — expand them into
    // virtual per-occurrence instances for the visible window first, so everything below
    // (timezone shift, midnight slicing, travel heuristics) operates on concrete instances.
    const range = visibleRange ?? {
      start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      end: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
    };
    const expandedEvents = expandRecurringEvents(events, range.start, range.end);

    // --- 1. PRE-SORT BY TIME ---
    const timeline = [...expandedEvents].sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    timeline.forEach((instance: EngineEvent) => {
      // --- 2. HYBRID PRIVACY & SOURCE FILTERING ---
      const eventSource = (instance as any).source || 'personal';
      const isOwner = instance.ownerId === currentUserId;
      
      /**
       * 🛡️ GOOGLE PRIVACY GATE
       * Personal data (/users/{uid}) is ONLY processed if the current user is the owner.
       * Shared data (/households/{hid}) is processed for all members.
       */
      if (eventSource === 'personal' && !isOwner) {
        console.warn("Security: Attempted to process unauthorized personal event.");
        return;
      }

      // --- 3. TEMPORAL NORMALIZATION ---
      const rawStartInput = (instance.start as any)?.toDate ? (instance.start as any).toDate() : instance.start;
      const rawEndInput = (instance.end as any)?.toDate ? (instance.end as any).toDate() : instance.end;

      const rawStart = new Date(rawStartInput);
      const rawEnd = new Date(rawEndInput);

      if (!isValid(rawStart) || !isValid(rawEnd)) return;

      // Shift to User's Wall Clock (Timezone aware)
      const displayStart = toDisplayDate(rawStart, userTimezone);
      const displayEnd = toDisplayDate(rawEnd, userTimezone);

      let displayInstance: EngineEvent = { 
        ...instance, 
        start: displayStart.toISOString(), 
        end: displayEnd.toISOString(),
        source: eventSource // Explicitly maintain source through processing
      };

      // --- 4. TRAVEL HEURISTIC INJECTION ---
      if (isTravelEnabled) {
        displayInstance = this.calculateTravelImpact(displayInstance, previousDisplayInstance, householdAddress, travelDurations);
      }
      previousDisplayInstance = displayInstance;

      // --- 5. MIDNIGHT SLICING ---
      processedInstances.push(...this.sliceEvent(displayInstance));
    });

    return processedInstances;
  }

  /**
   * 🚗 TRAVEL INTELLIGENCE HEURISTICS
   * Source-aware transit analysis. `travelDurations` (keyed "origin|destination", minutes) comes
   * from useTravelDurations.ts's Google Maps Distance Matrix lookups — when a real duration isn't
   * available yet (still loading, no location on either event, or the API call failed), falls
   * back to a flat 30-minute estimate rather than failing to compute anything at all.
   */
  private calculateTravelImpact(
    current: EngineEvent,
    prev: EngineEvent | null,
    homeAddress?: string | null,
    travelDurations?: Map<string, number>
  ): EngineEvent {
    let isImpossible = false;
    let travelBackHomeAdvised = true;
    let originType: 'home' | 'previous_event' = 'home';
    let estimatedTransitMins = 30; // fallback when no real duration is available for this pair

    const destinationAddress = (current as any).location?.address as string | undefined;
    const originAddress = prev
      ? ((prev as any).location?.address as string | undefined)
      : homeAddress ?? undefined;
    if (prev) originType = 'previous_event';

    if (destinationAddress && originAddress && travelDurations) {
      const key = `${originAddress}|${destinationAddress}`;
      const realDuration = travelDurations.get(key);
      if (typeof realDuration === 'number') {
        estimatedTransitMins = realDuration;
      }
    }

    if (prev && current.start && prev.end) { // Ensure start and end exist before calculating difference
      const gapMins = differenceInMinutes(new Date(current.start), new Date(prev.end));

      // Impossible Check: Gap is smaller than transit time
      if (gapMins < estimatedTransitMins) {
        isImpossible = true;
      }

      // Home Advice: Is gap > (Transit Home + Transit Back + 20 mins grace)?
      const totalTransitMins = estimatedTransitMins * 2;
      if (gapMins < (totalTransitMins + 20)) {
        travelBackHomeAdvised = false;
      }
    }

    return {
      ...current,
      travelMetadata: {
        estimatedDuration: estimatedTransitMins,
        originType,
        isImpossible,
        travelBackHomeAdvised
      }
    } as any;
  }

  /**
   * 🔪 MIDNIGHT SLICER
   */
  private sliceEvent(event: EngineEvent): EngineEvent[] {
    const slices: EngineEvent[] = [];
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    if (eventStart.getDate() === eventEnd.getDate() && 
        eventStart.getMonth() === eventEnd.getMonth() && 
        eventStart.getFullYear() === eventEnd.getFullYear()) {
      return [event]; // Event does not cross midnight, return as is
    }

    let cursor = new Date(eventStart);
    let safety = 0;

    while (cursor < eventEnd && safety < 31) {
      safety++; 

      const dayEnd = new Date(cursor);
      dayEnd.setHours(23, 59, 59, 999);
      const sliceEnd = eventEnd < dayEnd ? eventEnd : dayEnd;

      slices.push({
        ...event,
        id: `${event.id}-slice-${slices.length}`, // Unique ID for each slice
        start: new Date(cursor).toISOString(),
        end: new Date(sliceEnd).toISOString(),
        isSlice: true,
        isContinued: eventEnd > dayEnd, // Is there more of this event on subsequent days?
        isContinuation: cursor.getTime() > eventStart.getTime() // Is this a continuation from a previous day?
      });

      const nextDay = new Date(cursor);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);
      cursor = nextDay;
    }
    
    return slices;
  }
  
  /**
   * 🧬 OVERLAP ALGORITHM — thin wrapper kept for perspectiveUtils.ts's existing API;
   * see the standalone clusterOverlappingEvents() below for the actual logic (also used
   * directly by CalendarGrid.tsx, which has no CalendarEngine instance to call this on).
   */
  calculateLayoutGroups(events: EngineEvent[]): EngineEvent[][] {
    return clusterOverlappingEvents(events);
  }
}

/**
 * Classic interval-clustering: walk events in start-time order, growing a "cluster" of
 * mutually-touching events as long as each new event starts before the cluster's current
 * max end time (a running "high water mark", not just the immediately-previous event's end —
 * so A/B/C where A-C don't overlap but both overlap B still cluster together correctly).
 * Each returned group is one cluster; the caller lays a cluster's events out side-by-side,
 * each getting width = 1/cluster.length.
 */
export function clusterOverlappingEvents(events: EngineEvent[]): EngineEvent[][] {
    if (events.length === 0) return [];

    const sortedEvents = [...events].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const groups: EngineEvent[][] = [];

    let currentCluster: EngineEvent[] = [sortedEvents[0]];
    let clusterEndMs = new Date(sortedEvents[0].end).getTime();

    for (let i = 1; i < sortedEvents.length; i++) {
      const event = sortedEvents[i];
      const startMs = new Date(event.start).getTime();
      const endMs = new Date(event.end).getTime();

      if (startMs < clusterEndMs) {
        // Overlaps the cluster-so-far — join it and extend the high-water mark if longer.
        currentCluster.push(event);
        clusterEndMs = Math.max(clusterEndMs, endMs);
      } else {
        // No overlap with anything in the current cluster — close it and start a new one.
        groups.push(currentCluster);
        currentCluster = [event];
        clusterEndMs = endMs;
      }
    }
    groups.push(currentCluster);

    return groups;
}
